import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Search } from 'lucide-react';
import { isEachUnitItem } from '../../utils/programPricing';
import { marginPercentFromMultiplier } from '../../utils/alaCarteCatalog';

// Deliverables are either one-time or recurring per the client's monthly billing
// period. "Period" stores 'Month' so the per-client monthly allowance logic
// (isMonthlyDeliverablePeriod / getCurrentPeriod) keeps working.
const PERIOD_OPTIONS = [
  { value: 'One Time', label: 'One Time' },
  { value: 'Month', label: 'Period' },
];

// Anything that isn't one-time is a recurring "Period" (covers legacy
// Week/Quarter/Year values so the two-option select still resolves).
const isOneTimePeriod = (period) => !period || period === 'One Time';
const periodSelectValue = (period) => (isOneTimePeriod(period) ? 'One Time' : 'Month');
const periodLabel = (period) => (isOneTimePeriod(period) ? 'One Time' : 'Period');

// Packages and term pricing were retired July 2026 — legacy items keep their
// stored type (term items still render cost/unit below) but new picks are
// limited to these four.
const PRICING_TYPE_OPTIONS = [
  ['standard', 'Standard pricing'],
  ['child', 'Child'],
  ['caseByCase', 'Case by case'],
  ['alwaysIncluded', 'Always included'],
];

const SectionLabel = ({ children }) => (
  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
    {children}
  </p>
);

/**
 * Description, pricing type, deliverables, and price fields — shared between
 * the list-page "add" modal and the Overview tab on the item overview.
 *
 * Icon, name, department, and the Popular Add On flag live in the page/modal
 * headers, not here. Programs are assigned on the Programs template page, not
 * per catalog item. Pass `disabled` to render the whole form read-only.
 */
export default function AlaCarteCatalogStructureEditor({
  draft,
  setDraft,
  catalogItems = [],
  parentSearch,
  setParentSearch,
  disabled = false,
}) {
  const descriptionRef = useRef(null);

  const resizeDescription = useCallback(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 96)}px`;
  }, []);

  // `disabled` is a dep so the textarea re-measures when it mounts on entering
  // edit mode (the read-only branch renders no textarea to size).
  useLayoutEffect(() => {
    resizeDescription();
  }, [draft?.description, resizeDescription, disabled]);

  const parentEligibleItems = useMemo(
    () => catalogItems.filter((row) => row.pricingType !== 'child' && row.id && row.id !== draft?.id),
    [catalogItems, draft?.id]
  );

  const filteredParentItems = useMemo(
    () =>
      parentEligibleItems.filter((row) =>
        row.name.toLowerCase().includes((parentSearch || '').toLowerCase())
      ),
    [parentEligibleItems, parentSearch]
  );

  if (!draft) return null;

  // View mode: plain-text summaries instead of greyed-out form controls.
  if (disabled) {
    const pricingLabels = Object.fromEntries(PRICING_TYPE_OPTIONS);
    pricingLabels.term = 'Term pricing (legacy)';
    const parentName = catalogItems.find((r) => r.id === draft.parentItemId)?.name;
    const del = draft.deliverable || {};
    const cost = draft.standardPricing?.cost;
    const unit = draft.standardPricing?.unit || 'item';
    const isEach = isEachUnitItem(draft);
    const stripeId = (draft.standardPricing?.stripeProductId || '').trim();
    const money = (v) => `$${Number(v || 0).toLocaleString()}`;
    const textCls = 'text-sm text-gray-700 dark:text-gray-300';
    const viewMargin = marginPercentFromMultiplier(draft.caseByCaseMultiplier);

    return (
      <div className="space-y-6">
        <div>
          <SectionLabel>Description</SectionLabel>
          <p className={`whitespace-pre-wrap ${textCls}`}>{draft.description || '—'}</p>
        </div>

        <div>
          <SectionLabel>Pricing</SectionLabel>
          <p className={textCls}>
            {pricingLabels[draft.pricingType] || draft.pricingType}
            {draft.pricingType === 'child' && parentName ? ` — part of ${parentName}` : ''}
          </p>
          {draft.pricingType === 'caseByCase' && (
            <p className={`mt-1 ${textCls}`}>
              {viewMargin != null
                ? `Multiplier: ${draft.caseByCaseMultiplier}× · Margin: ${viewMargin.toFixed(1)}%`
                : 'No multiplier set'}
            </p>
          )}
        </div>

        <div>
          <SectionLabel>Deliverables</SectionLabel>
          <p className={textCls}>
            {draft.hasDeliverable
              ? [
                  String(del.quantity || '').trim() ? `Quantity: ${del.quantity}` : null,
                  String(del.maxQuantity || '').trim() ? `Max: ${del.maxQuantity}` : null,
                  periodLabel(del.period),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'No deliverables'}
            {del.trackParticipants ? ' · Tracks participants' : ''}
          </p>
        </div>

        {(draft.pricingType === 'standard' || draft.pricingType === 'term') && (
          <div>
            <SectionLabel>Price</SectionLabel>
            <p className={textCls}>
              {money(cost)} per {unit}
              {stripeId ? ` · Stripe: ${stripeId}` : ''}
            </p>
          </div>
        )}
        {(draft.pricingType === 'standard' || draft.pricingType === 'term' || draft.pricingType === 'caseByCase') && (
          <div>
            <SectionLabel>Minimum Term</SectionLabel>
            <p className={textCls}>
              {String(draft.minimumTermMonths ?? '').trim()
                ? `${draft.minimumTermMonths} ${Number(draft.minimumTermMonths) === 1 ? 'month' : 'months'}${
                    Number(cost) > 0 && !isEach
                      ? ` — in programs the price is spread monthly (${money(cost)} ÷ ${draft.minimumTermMonths})`
                      : ''
                  }`
                : 'None'}
              {isEach && Number(cost) > 0
                ? " — priced per each, so in programs the price spreads over the program's minimum term"
                : ''}
            </p>
          </div>
        )}
        {draft.pricingType === 'alwaysIncluded' && (
          <div>
            <SectionLabel>Price</SectionLabel>
            <p className={textCls}>{money(cost)}/month</p>
          </div>
        )}
      </div>
    );
  }

  const patchDraft = (partial) => setDraft((prev) => ({ ...prev, ...partial }));

  const updateStandardPricing = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      standardPricing: { ...(prev.standardPricing || { cost: '', unit: '' }), [field]: value },
    }));
  };

  const updateDeliverable = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      deliverable: { ...(prev.deliverable || { quantity: '', maxQuantity: '', period: 'One Time', programs: [] }), [field]: value },
    }));
  };

  const toggleDeliverable = (checked) => {
    setDraft((prev) => ({
      ...prev,
      hasDeliverable: checked,
      deliverable: checked
        ? prev.deliverable || { quantity: '', maxQuantity: '', period: 'One Time', programs: [] }
        : { quantity: '', maxQuantity: '', period: 'One Time', programs: [] },
    }));
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800';
  const checkboxCls = 'h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500';

  const showStandardFields = draft.pricingType === 'standard' || draft.pricingType === 'term';
  const caseByCaseMargin = marginPercentFromMultiplier(draft.caseByCaseMultiplier);

  return (
    <fieldset disabled={disabled} className="m-0 min-w-0 space-y-6 border-0 p-0">
      {/* Description */}
      <div>
        <SectionLabel>Description</SectionLabel>
        <textarea
          ref={descriptionRef}
          value={draft.description || ''}
          onChange={(e) => patchDraft({ description: e.target.value })}
          rows={1}
          className={`min-h-24 resize-none overflow-hidden ${inputCls}`}
        />
      </div>

      {/* Pricing type */}
      <div>
        <SectionLabel>Pricing</SectionLabel>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {PRICING_TYPE_OPTIONS.map(([value, label]) => (
            <label key={value} className="flex items-center">
              <input
                type="radio"
                name="catalogPricingType"
                value={value}
                checked={draft.pricingType === value}
                onChange={() => {
                  setParentSearch('');
                  patchDraft({
                    pricingType: value,
                    parentItemId: value === 'child' ? draft.parentItemId || '' : draft.parentItemId,
                  });
                }}
                className="mr-2 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
            </label>
          ))}
        </div>
        {draft.pricingType === 'term' && (
          <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            This item uses legacy term pricing — its cost and unit still apply below. Pick a pricing type above to
            migrate it.
          </p>
        )}
        {draft.pricingType === 'child' && (
          <div className="mt-3">
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Parent item</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search parent items..."
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700">
              {filteredParentItems.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  {parentSearch ? 'No matching items' : 'No parent items available'}
                </p>
              ) : (
                filteredParentItems.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => patchDraft({ parentItemId: row.id })}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-600 ${
                      draft.parentItemId === row.id
                        ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {row.name}
                  </button>
                ))
              )}
            </div>
            {draft.parentItemId && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Selected: {parentEligibleItems.find((p) => p.id === draft.parentItemId)?.name}
              </p>
            )}
          </div>
        )}
        {draft.pricingType === 'caseByCase' && (
          <div className="mt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The BBA team will connect to establish an estimate.
            </p>
            <div className="mt-3 max-w-xs">
              <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Multiplier</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 2.5"
                value={draft.caseByCaseMultiplier ?? ''}
                onChange={(e) => patchDraft({ caseByCaseMultiplier: e.target.value })}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Our markup on the internal cost — the bid price is cost × multiplier.
              </p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Margin:{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {caseByCaseMargin != null ? `${caseByCaseMargin.toFixed(1)}%` : '—'}
                </span>
              </p>
            </div>
          </div>
        )}
        {draft.pricingType === 'alwaysIncluded' && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Enter the price per month for billing.
          </p>
        )}
      </div>

      {/* Deliverables */}
      <div>
        <SectionLabel>Deliverables</SectionLabel>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={draft.hasDeliverable || false}
              disabled={draft.alwaysRequired === true}
              onChange={(e) => toggleDeliverable(e.target.checked)}
              className={checkboxCls}
            />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Has deliverables</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={draft.deliverable?.trackParticipants || false}
              onChange={(e) => updateDeliverable('trackParticipants', e.target.checked)}
              className={checkboxCls}
            />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Track participants</span>
          </label>
        </div>
        {draft.hasDeliverable && (
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Quantity</label>
              <input
                type="text"
                value={draft.deliverable?.quantity || ''}
                disabled={draft.alwaysRequired === true}
                onChange={(e) => updateDeliverable('quantity', e.target.value)}
                className={inputCls}
              />
              {draft.alwaysRequired === true && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Always required — locked at 1.</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Maximum quantity</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="No limit"
                value={draft.deliverable?.maxQuantity ?? ''}
                disabled={draft.alwaysRequired === true}
                onChange={(e) => updateDeliverable('maxQuantity', e.target.value)}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Caps how many a client can select (optional).</p>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Period</label>
              <select
                value={periodSelectValue(draft.deliverable?.period)}
                onChange={(e) => updateDeliverable('period', e.target.value)}
                className={inputCls}
              >
                {PERIOD_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Price */}
      {showStandardFields ? (
        <div>
          <SectionLabel>Price</SectionLabel>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Cost</label>
              <input
                type="number"
                value={draft.standardPricing?.cost ?? ''}
                onChange={(e) => updateStandardPricing('cost', e.target.value)}
                className={inputCls}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Unit</label>
              <input
                type="text"
                placeholder="hour, item, etc."
                value={draft.standardPricing?.unit || ''}
                onChange={(e) => updateStandardPricing('unit', e.target.value)}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                A unit of <span className="font-medium">each</span> spreads the price across the program's minimum
                term instead of this item's own.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Stripe product ID (optional)</label>
              <input
                type="text"
                placeholder="prod_XXXXXXXXXXXXXX"
                value={draft.standardPricing?.stripeProductId || ''}
                onChange={(e) => updateStandardPricing('stripeProductId', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>
      ) : draft.pricingType === 'alwaysIncluded' ? (
        <div>
          <SectionLabel>Price</SectionLabel>
          <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Cost ($/month)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.standardPricing?.cost ?? ''}
            onChange={(e) => updateStandardPricing('cost', e.target.value)}
            className={`max-w-xs ${inputCls}`}
          />
        </div>
      ) : null}

      {/* Minimum term — priced items spread the price in programs; case-by-case
          terms come from the bid, but still raise the program's minimum. */}
      {(showStandardFields || draft.pricingType === 'caseByCase') && (
        <div>
          <SectionLabel>Minimum Term</SectionLabel>
          <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Minimum term (months, optional)</label>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            placeholder="None"
            value={draft.minimumTermMonths ?? ''}
            onChange={(e) => patchDraft({ minimumTermMonths: e.target.value })}
            className={`max-w-xs ${inputCls}`}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The price covers the full minimum term. In custom programs the monthly total adds price ÷ months, and
            the program's minimum term becomes the highest of any included service. Items priced per{' '}
            <span className="font-medium">each</span> ignore this and spread over that program minimum instead.
          </p>
        </div>
      )}
    </fieldset>
  );
}
